"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { readConsent, CONSENT_EVENT } from "@/lib/consent";
import { META_PIXEL_READY_EVENT } from "@/lib/meta-pixel";

const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

/**
 * Meta Pixel (Facebook/Instagram), gekoppeld aan de bestaande Pixel-ID.
 * AVG-veilig: laadt PAS na expliciete marketing-toestemming (aparte
 * categorie van "analytics", zie lib/consent.ts) — zonder toestemming wordt
 * er niets geladen en geen ping verstuurd. Trekt de bezoeker toestemming in,
 * dan stopt het laden bij de volgende paginaweergave.
 *
 * fbq volgt App Router-navigaties niet automatisch (geen History-API-hook
 * zoals gtag.js die heeft) — vandaar de expliciete PageView per pathname-
 * wijziging hieronder.
 */
export default function MetaPixel() {
  const [allowed, setAllowed] = useState(false);
  const [ready, setReady] = useState(false);
  const pathname = usePathname();
  const firstPageView = useRef(true);

  useEffect(() => {
    const update = () => setAllowed(!!readConsent()?.marketing);
    update();
    window.addEventListener(CONSENT_EVENT, update);
    return () => window.removeEventListener(CONSENT_EVENT, update);
  }, []);

  useEffect(() => {
    // Basisscript vuurt zelf al één PageView bij het laden (init-code
    // hieronder) — die niet dubbel versturen bij de allereerste render.
    if (!ready) return;
    if (firstPageView.current) { firstPageView.current = false; return; }
    window.fbq?.("track", "PageView");
  }, [pathname, ready]);

  // Events die vuurden vóór fbq bestond (lib/meta-pixel.ts) gaan hier alsnog weg.
  // next/script meldt onReady voordat het inline script is uitgevoerd (het element
  // komt pas daarna in de pagina), dus dáár kan het nog niet. Dit effect draait na
  // de render die erop volgt, wanneer fbq bestaat; de herhaling is een vangnet.
  useEffect(() => {
    if (!ready) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      if (typeof window.fbq === "function") window.dispatchEvent(new Event(META_PIXEL_READY_EVENT));
      else if (tries++ < 20) timer = setTimeout(tick, 50);
    };
    tick();
    return () => clearTimeout(timer);
  }, [ready]);

  if (!PIXEL_ID || !allowed) return null;

  return (
    <>
      <Script id="meta-pixel-init" strategy="afterInteractive" onReady={() => setReady(true)}>
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${PIXEL_ID}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}
